import type {UseQueryResult} from '@tanstack/react-query';
import {useQuery} from '@tanstack/react-query';
import useApiFetch from '@/components/MSAL/useApiFetch';
import type {EmployeeActuals} from '@/types/Actuals';
import type {CapacityConfig} from '@/types/CapacityConfig';
import type {Employee} from '@/types/Employee';
import {apiUrl} from '@/utils/api';

type Props = {
    fiscalMonth : string;
    months : number;
    capacityConfig : CapacityConfig;
    employees : Employee[];
};

export const useGetActuals = ({fiscalMonth, months, capacityConfig, employees} : Props)
: UseQueryResult<EmployeeActuals[]> => {
    const fetch = useApiFetch();

    const employeeCache : number = employees.length == 1 ? employees[0].id : employees.length;

    return useQuery(
        [
            'useGetActuals',
            `useGetActuals-s-${fiscalMonth}`,
            `useGetActuals-e-${months}`,
            `useGetActuals-e-${employeeCache}`,
        ],
        async ({signal}) : Promise<EmployeeActuals[]> => {
            const url = apiUrl('/v1/capacity/actuals');
            const configMonthKeys = Object.keys(capacityConfig);
            const startIndex = configMonthKeys.findIndex(
                c => capacityConfig[c].fiscalstart === capacityConfig[fiscalMonth].fiscalstart
            );
            let endingIndex = startIndex + months;

            if (endingIndex > configMonthKeys.length) {
                endingIndex = configMonthKeys.length - 1;
            }

            const response = await fetch(
                url,
                {
                    signal,
                    method: 'POST',
                    body: JSON.stringify({
                        dateStart: capacityConfig[fiscalMonth].fiscalstart,
                        dateEnd: capacityConfig[configMonthKeys[endingIndex]].fiscalend,
                        associateIds: employees.map(e => e.id),
                    }),
                }
            );

            if (!response.ok) {
                throw new Error('Unable to fetch actuals.');
            }

            return await response.json() as EmployeeActuals[];
        }
    );
};
